/** Presentation helpers shared by the Background Tasks grid + details dialog. */
import { Box, Chip, LinearProgress, Link, Tooltip, Typography } from '@mui/material';
import type { ChipProps } from '@mui/material';

import { TASK_STATE, type BackgroundTaskRecord, type TaskProgress } from '@/api/bgtasks';

export function stateChipColor(state: number): ChipProps['color'] {
  switch (state) {
    case TASK_STATE.PENDING: return 'default';
    case TASK_STATE.RUNNING: return 'info';
    case TASK_STATE.COMPLETED: return 'success';
    case TASK_STATE.FAILED: return 'error';
    case TASK_STATE.CANCELLED: return 'warning';
    case TASK_STATE.AWAITING_APPROVAL: return 'secondary';
    default: return 'default';
  }
}

export function TaskStateChip({ task }: { task: BackgroundTaskRecord }) {
  const lost = task.state === TASK_STATE.RUNNING && task.health?.status === 'lost';
  return (
    <Chip
      size="small"
      label={lost ? 'Running (worker lost)' : task.state_display}
      color={lost ? 'error' : stateChipColor(task.state)}
      variant={task.state === TASK_STATE.PENDING ? 'outlined' : 'filled'}
    />
  );
}

/** The progress bar + label, shared by the grid cell, the details dialog and
 *  the inline TaskProgressCard (it used to be hand-rolled in each).
 *
 *  A task that knows its total gets a determinate bar and a percentage. One
 *  that does not (`total` 0 — a catalogue fetch counts rows it cannot know in
 *  advance) gets an indeterminate bar and the plain count: a determinate 0%
 *  would read as "stuck" while it is working. */
export function TaskProgressBar({
  progress,
  showCounts = false,
  height = 6,
}: {
  progress: TaskProgress;
  /** true → "current/total (pct%)" (dialog style); false → "pct%" (grid style). */
  showCounts?: boolean;
  height?: number;
}) {
  const known = progress.total > 0;
  let label: string;
  if (!known) label = progress.current > 0 ? String(progress.current) : '';
  else if (showCounts) label = `${progress.current}/${progress.total} (${progress.pct}%)`;
  else label = `${progress.pct}%`;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <LinearProgress
        variant={known ? 'determinate' : 'indeterminate'}
        value={known ? Math.min(progress.pct, 100) : undefined}
        sx={{ flex: 1, height, borderRadius: 1 }}
      />
      {label && <Typography variant={showCounts ? 'body2' : 'caption'}>{label}</Typography>}
    </Box>
  );
}

/** Log-level → text color (theme palette keys). */
export function levelColor(levelname?: string): string {
  switch ((levelname ?? '').toUpperCase()) {
    case 'CRITICAL':
    case 'ERROR': return 'error.main';
    case 'WARNING': return 'warning.main';
    case 'DEBUG': return 'text.disabled';
    default: return 'text.primary';
  }
}

/** Local time with the timezone name — audit-grid convention. */
export const fmtTs = (value?: string | null) =>
  value ? new Date(value).toLocaleString(undefined, { timeZoneName: 'short' }) : '—';

/** UTC rendering for tooltips/popovers — audit-grid convention. */
export const utcTs = (value: string) =>
  `${new Date(value).toISOString().slice(0, 19).replace('T', ' ')} UTC`;

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1],
];
const relFmt = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

export function relativeTime(iso: string): string {
  const diffS = (new Date(iso).getTime() - Date.now()) / 1000;
  for (const [unit, secs] of RELATIVE_UNITS) {
    if (Math.abs(diffS) >= secs || unit === 'second') {
      return relFmt.format(Math.round(diffS / secs), unit);
    }
  }
  return '';
}

/** Localized timestamp with a UTC tooltip — the console-wide convention. */
export function TsValue({ value }: { value?: string | null }) {
  if (!value) return <>—</>;
  return (
    <Tooltip title={utcTs(value)} placement="top">
      <span>{fmtTs(value)}</span>
    </Tooltip>
  );
}

const EMPTY_PARAM_VALUES = new Set(['', 'null', 'undefined', '{}', '[]']);

/** Non-empty task arguments as display pairs (null/empty values dropped).
 *  Returns null when params isn't a plain object — callers fall back to the
 *  raw JSON string. */
export function paramsEntries(
  params: unknown,
): [string, string][] | null {
  if (!params || typeof params !== 'object' || Array.isArray(params)) return null;
  const out: [string, string][] = [];
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value === null || value === undefined || value === '') continue;
    const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
    if (EMPTY_PARAM_VALUES.has(text)) continue;
    out.push([key, text]);
  }
  return out;
}

/** Short human summary of task arguments: date range, ID counts, flags.
 *  Null when no summary can be built (caller falls back to raw text). */
export function paramsSummary(params: unknown): string | null {
  if (!params || typeof params !== 'object' || Array.isArray(params)) return null;
  const p = params as Record<string, unknown>;
  const bits: string[] = [];

  if (p.integration_name) {
    bits.push(p.account ? `${String(p.integration_name)}/${String(p.account)}` : String(p.integration_name));
  }
  if (p.from_date || p.to_date) {
    bits.push(`${String(p.from_date ?? '…')} to ${String(p.to_date ?? '…')}`);
  }
  if (p.updated_from || p.updated_to) {
    bits.push(`updated: ${String(p.updated_from ?? '…')} to ${String(p.updated_to ?? 'now')}`);
  }
  if (p.created_from || p.created_to) {
    bits.push(`created: ${String(p.created_from ?? '…')} to ${String(p.created_to ?? 'now')}`);
  }
  const idList = (key: string, noun: string) => {
    const v = p[key];
    if (!Array.isArray(v) || v.length === 0) return;
    bits.push(v.length <= 2 ? v.map(String).join(', ') : `${v.length} ${noun}`);
  };
  idList('product_ids', 'products');
  idList('booking_ids', 'bookings');
  idList('order_ids', 'orders');
  idList('user_ids', 'users');
  idList('user_emails', 'emails');
  if (Array.isArray(p.rows) && p.rows.length) bits.push(`${p.rows.length} rows`);
  if (Array.isArray(p.statuses) && p.statuses.length) {
    bits.push(`statuses: ${p.statuses.map(String).join(',')}`);
  }
  if (p.dry_run === true) bits.push('dry run');
  if (p.reconcile_existing === true) bits.push('reconcile');
  if (p.processing_mode === 'inline') bits.push('inline');
  if (typeof p.import_delay === 'number' && p.import_delay > 0) {
    bits.push(`delay ${p.import_delay}s`);
  }

  if (!bits.length) {
    // No recognized keys — summarize by count of non-empty arguments.
    const entries = paramsEntries(params);
    if (!entries) return null;
    return entries.length ? `${entries.length} arguments` : null;
  }
  return bits.join(', ');
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

const URL_SPLIT_RE = /(https?:\/\/[^\s<>"')\]]+)/g;

/** Render text with http(s) URLs as safe links — no HTML injection, the text
 *  is split and URLs become real <Link> elements. */
export function LinkifiedText({ text }: { text: string }) {
  const parts = text.split(URL_SPLIT_RE);
  if (parts.length === 1) return <>{text}</>;
  return (
    <>
      {parts.map((part, i) =>
        /^https?:\/\//.test(part) ? (
          <Link key={i} href={part} target="_blank" rel="noopener noreferrer">
            {part}
          </Link>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}
